import { supabase } from './supabase.js';
import { compressImageForComunidad } from './storageService.js';

export const COMUNIDAD_IMAGENES_BUCKET = 'comunidad-imagenes';

const MAX_BYTES_AFTER_COMPRESS = 3 * 1024 * 1024;

export function isHttpImagenUrl(url) {
  const s = String(url ?? '').trim();
  return s.startsWith('http://') || s.startsWith('https://');
}

/** Path en bucket desde URL pública de Supabase Storage. */
export function storageObjectPathFromComunidadImagenPublicUrl(url) {
  const s = String(url ?? '').trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    const marker = `/object/public/${COMUNIDAD_IMAGENES_BUCKET}/`;
    const i = u.pathname.indexOf(marker);
    if (i === -1) return null;
    const raw = u.pathname.slice(i + marker.length);
    return raw ? decodeURIComponent(raw) : null;
  } catch {
    return null;
  }
}

export function isComunidadImagenInOurStorage(url) {
  return !!storageObjectPathFromComunidadImagenPublicUrl(url);
}

/**
 * Admin: sube cartel a Storage (misma ruta que al aprobar desde n8n o users/{authorId}).
 * @param {File} file
 * @param {string} publicationId
 * @param {{ authorId?: string|null }} [opts]
 */
export async function uploadComunidadImagenAdmin(file, publicationId, { authorId = null } = {}) {
  if (!file || !publicationId) return { url: null, error: 'Falta archivo o id de publicación.' };

  const compressed = await compressImageForComunidad(file);
  if (compressed.size > MAX_BYTES_AFTER_COMPRESS) {
    return {
      url: null,
      error: 'La imagen sigue siendo demasiado grande tras optimizarla. Prueba otra más pequeña.',
    };
  }

  const rawExt =
    (compressed.name && compressed.name.includes('.') ? compressed.name.split('.').pop() : '') || 'webp';
  const ext = /^[a-z0-9]+$/i.test(rawExt) ? rawExt.toLowerCase() : 'webp';
  const prefix = authorId ? `users/${authorId}` : 'n8n';
  const objectPath = `${prefix}/${publicationId}-${Date.now()}.${ext}`;

  const { error } = await supabase.storage.from(COMUNIDAD_IMAGENES_BUCKET).upload(objectPath, compressed, {
    cacheControl: '86400',
    upsert: false,
    contentType: compressed.type || 'image/webp',
  });

  if (error) {
    console.error('[uploadComunidadImagenAdmin]', error.message);
    return { url: null, error: error.message };
  }

  const { data } = supabase.storage.from(COMUNIDAD_IMAGENES_BUCKET).getPublicUrl(objectPath);
  return { url: data?.publicUrl || null, error: data?.publicUrl ? null : 'No se obtuvo URL pública.' };
}

/**
 * @param {File} file
 * @param {string} userId
 * @param {string|null} [publicationId]
 * @returns {Promise<{ url: string|null, error: string|null }>}
 */
export async function uploadComunidadImagen(file, userId, publicationId = null) {
  if (!file || !userId) return { url: null, error: 'missing_file_or_user' };

  const compressed = await compressImageForComunidad(file);
  if (compressed.size > MAX_BYTES_AFTER_COMPRESS) {
    return {
      url: null,
      error: 'La imagen sigue siendo demasiado grande tras optimizarla. Prueba otra más pequeña.',
    };
  }

  const rawExt =
    (compressed.name && compressed.name.includes('.') ? compressed.name.split('.').pop() : '') || 'webp';
  const ext = /^[a-z0-9]+$/i.test(rawExt) ? rawExt.toLowerCase() : 'webp';
  const folder = publicationId ? String(publicationId) : `draft_${Date.now()}`;
  const objectPath = `${userId}/${folder}/${Date.now()}.${ext}`;

  const { error } = await supabase.storage.from(COMUNIDAD_IMAGENES_BUCKET).upload(objectPath, compressed, {
    cacheControl: '86400',
    upsert: false,
    contentType: compressed.type || 'image/webp',
  });

  if (error) {
    console.error('[uploadComunidadImagen]', error.message);
    return { url: null, error: error.message };
  }

  const { data } = supabase.storage.from(COMUNIDAD_IMAGENES_BUCKET).getPublicUrl(objectPath);
  return { url: data?.publicUrl || null, error: data?.publicUrl ? null : 'no_public_url' };
}

async function fileFromDataUrl(dataUrl) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
  return new File([blob], `comunidad.${type === 'image/png' ? 'png' : 'jpg'}`, { type });
}

/**
 * URL final para guardar en `cuerpo.imagenUrl` (nunca data:/blob: en BD).
 * @param {{ userId: string, publicationId?: string|null, pendingFile?: File|null, previewUrl?: string|null }} p
 */
export async function resolveComunidadImagenUrlForSave({
  userId,
  publicationId = null,
  pendingFile = null,
  previewUrl = null,
}) {
  if (pendingFile) {
    const { url, error } = await uploadComunidadImagen(pendingFile, userId, publicationId);
    if (error || !url) throw new Error(error || 'No se pudo subir la imagen.');
    return url;
  }

  const preview = String(previewUrl ?? '').trim();
  if (!preview) return null;
  if (isHttpImagenUrl(preview)) return preview;

  if (preview.startsWith('data:image/')) {
    const file = await fileFromDataUrl(preview);
    const { url, error } = await uploadComunidadImagen(file, userId, publicationId);
    if (error || !url) throw new Error(error || 'No se pudo migrar la imagen al almacenamiento.');
    return url;
  }

  if (preview.startsWith('blob:')) return null;
  return null;
}
